/**
 * responsiveCss — pure CSS generator for per-section responsive overrides.
 *
 * buildSectionCss(scopeKey, responsive) → string ("" when nothing to emit).
 *
 * `responsive` shape: { desktop|mobile: { elements, spacing, image_position, images } }
 *   - elements.{el}: fontSize / color / fontWeight / letterSpacing / textAlign / lineHeight
 *     + element-only box/layout props: height / padding / flexDirection / gap /
 *     marginTop / justifyContent
 *     + element-only visibility/paint props (public site only):
 *       hidden (bool → display:none on that breakpoint) / zIndex (int -50..9999) /
 *       background (string, anti-injection guarded) / opacity (0..1)
 *   - spacing: paddingTop / paddingBottom / paddingX / marginTop / marginBottom / background
 *   - image_position (legacy): applied to every [data-img] in the section —
 *     suppressed for a bucket as soon as that bucket has per-field `images`
 *     entries (the two systems would fight over object-position otherwise)
 *   - images.{contentField}: { position: "50% 25%", scale: 1.15 } applied to [data-img="field"]
 *     Position percentages may lie OUTSIDE [0,100] (sanity range -100..200):
 *     the overshoot is rendered as an extra translate() so the image can be
 *     over-panned past the cover-fit limit, revealing the section background.
 *
 * Buckets are emitted in a FIXED order (mobile, then desktop) and BOTH are
 * media-scoped so they can never leak into each other regardless of object
 * key order: mobile rules are wrapped in `@media (max-width: 1023.98px)`,
 * desktop rules in `@media (min-width: 1024px)` (Tailwind lg: breakpoint).
 */
const clampNum = (v, min, max) => Math.min(max, Math.max(min, v));
const round2 = (v) => Math.round(v * 100) / 100;

// Cheap injection guard: a declaration value must never be able to close the
// declaration block or smuggle extra declarations into the stylesheet. It also
// must not carry `<`/`>` — the generated CSS is injected into a <style> tag via
// dangerouslySetInnerHTML, so a value like `red</style><script>…` could break
// out of the element. Valid CSS values (colors, px, gradients) never contain
// `<`/`>`, so rejecting them here costs nothing.
const isUnsafeValue = (v) => /[{};<>]/.test(String(v));

// Push `prop: value !important` unless the value is empty or unsafe.
const pushDecl = (arr, prop, value) => {
  if (value === undefined || value === null || value === "") return;
  if (isUnsafeValue(value)) return;
  arr.push(`${prop}: ${value} !important`);
};

const BUCKET_ORDER = ["mobile", "desktop"];

export function buildSectionCss(scopeKey, responsive) {
  if (!responsive || typeof responsive !== "object") return "";
  const chunks = [];

  for (const bp of BUCKET_ORDER) {
    const conf = responsive[bp];
    if (!conf || typeof conf !== "object") continue;
    // NOTE: concatenation on purpose — String.replace would mangle "$" sequences in values.
    const wrap =
      bp === "desktop"
        ? (rule) => `@media (min-width: 1024px) { ${rule} }`
        : (rule) => `@media (max-width: 1023.98px) { ${rule} }`;

    // Per-element style rules
    const elements = conf.elements || {};
    for (const [el, style] of Object.entries(elements)) {
      if (!style || typeof style !== "object") continue;
      const decl = [];
      pushDecl(decl, "font-size", style.fontSize);
      pushDecl(decl, "color", style.color);
      pushDecl(decl, "font-weight", style.fontWeight);
      pushDecl(decl, "letter-spacing", style.letterSpacing);
      pushDecl(decl, "text-align", style.textAlign);
      pushDecl(decl, "line-height", style.lineHeight);
      // Box/layout props apply ONLY to the element itself — on descendants
      // (icon, span interior de buton) ar dubla padding-ul / ar strica layout-ul.
      const boxDecl = [];
      // Un override explicit de width/height trebuie să câștige și în fața unui
      // max-w-*/max-h-* Tailwind de pe ACELAȘI nod (ex. Hero.jsx max-w-[470px]):
      // `width: Npx !important` NU învinge `max-width` — sunt proprietăți
      // diferite. Neutralizăm max-/min- DOAR când există override, ca secțiunile
      // neatinse să-și păstreze layout-ul implicit (Tailwind) neschimbat.
      if (style.width !== undefined && style.width !== null && style.width !== "" && !isUnsafeValue(style.width)) {
        boxDecl.push(`width: ${style.width} !important`);
        boxDecl.push("max-width: none !important");
        boxDecl.push("min-width: 0 !important");
      }
      if (style.height !== undefined && style.height !== null && style.height !== "" && !isUnsafeValue(style.height)) {
        boxDecl.push(`height: ${style.height} !important`);
        boxDecl.push("max-height: none !important");
        boxDecl.push("min-height: 0 !important");
      }
      pushDecl(boxDecl, "padding", style.padding);
      pushDecl(boxDecl, "flex-direction", style.flexDirection);
      pushDecl(boxDecl, "gap", style.gap);
      pushDecl(boxDecl, "margin-top", style.marginTop);
      pushDecl(boxDecl, "justify-content", style.justifyContent);
      // Visibility / paint — element-only, public site only. In editor the
      // element stays visible (handled by the PreviewMode agent), here we emit
      // the real breakpoint override for the rendered page.
      if (style.hidden === true) {
        boxDecl.push("display: none !important");
      }
      {
        // z-index: coerce to a finite integer, clamp -50..9999. No `position`
        // is forced — buttons are flex items and the gradient is absolute;
        // both honour z-index without it.
        const zRaw = typeof style.zIndex === "number" ? style.zIndex : parseFloat(style.zIndex);
        if (Number.isFinite(zRaw)) {
          boxDecl.push(`z-index: ${clampNum(Math.trunc(zRaw), -50, 9999)} !important`);
        }
      }
      // background: string value, guarded against injection by pushDecl.
      pushDecl(boxDecl, "background", style.background);
      {
        // Glisare gradient (background-position) — pentru overlay-uri full-cover
        // care se „mută" fără a translata cutia (deci fără margini expuse). % 0..100.
        const bx = typeof style.bgx === "number" ? style.bgx : parseFloat(style.bgx);
        if (Number.isFinite(bx)) boxDecl.push(`background-position-x: ${clampNum(round2(bx), 0, 100)}% !important`);
        const by = typeof style.bgy === "number" ? style.bgy : parseFloat(style.bgy);
        if (Number.isFinite(by)) boxDecl.push(`background-position-y: ${clampNum(round2(by), 0, 100)}% !important`);
      }
      {
        // opacity: coerce to a finite number, clamp 0..1.
        const oRaw = typeof style.opacity === "number" ? style.opacity : parseFloat(style.opacity);
        if (Number.isFinite(oRaw)) {
          boxDecl.push(`opacity: ${round2(clampNum(oRaw, 0, 1))} !important`);
        }
      }
      // Free-transform: mutare (tx/ty px), mărire (scale) și rotire (rotate deg),
      // APLICATE DOAR pe element (nu pe descendenți — altfel s-ar compune de două ori).
      // Valorile sunt numerice → construim declarația manual, cu clamp de siguranță.
      {
        const numOr = (v, d) => {
          const n = typeof v === "number" ? v : parseFloat(v);
          return Number.isNaN(n) ? d : n;
        };
        const tx = clampNum(numOr(style.tx, 0), -4000, 4000);
        const ty = clampNum(numOr(style.ty, 0), -4000, 4000);
        let sc = numOr(style.scale, 1);
        sc = sc > 0 ? clampNum(sc, 0.1, 6) : 1;
        const rot = clampNum(numOr(style.rotate, 0), -360, 360);
        const tParts = [];
        if (tx !== 0 || ty !== 0) tParts.push(`translate(${round2(tx)}px, ${round2(ty)}px)`);
        if (sc !== 1) tParts.push(`scale(${round2(sc)})`);
        if (rot !== 0) tParts.push(`rotate(${round2(rot)}deg)`);
        if (tParts.length) {
          boxDecl.push(`transform: ${tParts.join(" ")} !important`);
          boxDecl.push("transform-origin: center !important");
        }
      }
      if (decl.length === 0 && boxDecl.length === 0) continue;
      if (decl.length) {
        const rule = `[data-section-key="${scopeKey}"] [data-el="${el}"], [data-section-key="${scopeKey}"] [data-el="${el}"] * { ${decl.join("; ")} }`;
        chunks.push(wrap(rule));
      }
      if (boxDecl.length) {
        const rule = `[data-section-key="${scopeKey}"] [data-el="${el}"] { ${boxDecl.join("; ")} }`;
        chunks.push(wrap(rule));
      }
    }

    // Spacing on the section wrapper itself
    const spacing = conf.spacing || {};
    const spaceDecl = [];
    pushDecl(spaceDecl, "padding-top", spacing.paddingTop);
    pushDecl(spaceDecl, "padding-bottom", spacing.paddingBottom);
    if (spacing.paddingX && !isUnsafeValue(spacing.paddingX)) {
      spaceDecl.push(`padding-left: ${spacing.paddingX} !important`);
      spaceDecl.push(`padding-right: ${spacing.paddingX} !important`);
    }
    pushDecl(spaceDecl, "margin-top", spacing.marginTop);
    pushDecl(spaceDecl, "margin-bottom", spacing.marginBottom);
    pushDecl(spaceDecl, "background", spacing.background);
    if (spaceDecl.length) {
      // Target the section wrapper itself, the first <section> inside, OR a
      // wrapper that IS the section element.
      const rule = `[data-section-key="${scopeKey}"] > section, [data-section-key="${scopeKey}"][data-section-self], section[data-section-key="${scopeKey}"] { ${spaceDecl.join("; ")} }`;
      chunks.push(wrap(rule));
    }

    // Legacy single image position for the whole section — suppressed as soon
    // as this bucket has per-field `images` entries (they own object-position).
    const imageEntries = Object.entries(conf.images || {});
    if (conf.image_position && imageEntries.length === 0 && !isUnsafeValue(conf.image_position)) {
      chunks.push(wrap(`[data-section-key="${scopeKey}"] [data-img] { object-position: ${conf.image_position} !important; }`));
    }

    // Per-image-field position + zoom, with over-pan support.
    // object-position alone can only pan along an axis where the cover-fit
    // image naturally overflows its box, and only within [0,100]. Position
    // values outside [0,100] (sanity range -100..200) encode over-pan: the
    // clamped value feeds object-position, the overshoot becomes an extra
    // translate() (positive overshoot = image shifts right/down, revealing
    // the section background on the opposite side). On top of that, zoom
    // blending: translate % is relative to the untransformed border box and
    // the max safe zoom shift is (s-1)*50%, hence t = (50-p)*(s-1).
    for (const [field, img] of imageEntries) {
      if (!img || typeof img !== "object") continue;
      let scale = typeof img.scale === "number" ? img.scale : parseFloat(img.scale);
      if (Number.isNaN(scale) || scale <= 0) scale = 1;
      const hasZoom = scale > 1;
      if (!img.position && !hasZoom) continue;
      const pos = [50, 50];
      if (typeof img.position === "string") {
        const parts = img.position.trim().split(/\s+/);
        for (let i = 0; i < 2; i++) {
          const p = parts[i];
          if (p && p.endsWith("%")) {
            const n = parseFloat(p);
            if (!Number.isNaN(n)) pos[i] = clampNum(n, -100, 200);
          }
        }
      }
      const clamped = [0, 0];
      const t = [0, 0];
      for (let i = 0; i < 2; i++) {
        const raw = pos[i];
        const c = clampNum(raw, 0, 100);
        const overshoot = raw < 0 ? -raw : raw > 100 ? -(raw - 100) : 0;
        clamped[i] = round2(c);
        t[i] = round2((50 - c) * (scale - 1) + overshoot);
      }
      let decl = "";
      if (img.position) decl += `object-position: ${clamped[0]}% ${clamped[1]}% !important;`;
      if (t[0] !== 0 || t[1] !== 0 || hasZoom) {
        if (decl) decl += " ";
        decl += `transform: translate(${t[0]}%, ${t[1]}%) scale(${scale}) !important; transform-origin: center !important;`;
      }
      chunks.push(wrap(`[data-section-key="${scopeKey}"] [data-img="${field}"] { ${decl} }`));
    }
  }

  return chunks.length ? chunks.join("\n") : "";
}
